import { useEffect } from "react";
import { motion } from "framer-motion";
import Lenis from "lenis";

import BrandLogo from "@/components/BrandLogo";
import QuoteForm from "@/components/QuoteForm";
import {
  SectionReveal,
  fadeUp,
  staggerContainer,
} from "@/components/SectionReveal";
import { Button } from "@/components/ui/button";

const HERO_BLUEPRINT =
  "https://images.unsplash.com/photo-1712696767893-fec648e0b387?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxhcmNoaXRlY3R1cmFsJTIwYmx1ZXByaW50JTIwY2xvc2UlMjB1cCUyMGRldGFpbHxlbnwwfHx8fDE3NzI4ODIzMDh8MA&ixlib=rb-4.1.0&q=85";

const philosophyPoints = [
  "Correct sequencing of work to save time",
  "Continuous quality checks during construction",
  "Transparent project coordination",
  "Data-driven decision making",
];

const differentiators = [
  {
    number: "01",
    title: "Micro-Level Project Coordination",
    description:
      "Every stage of construction is carefully planned and monitored to ensure the correct order of work, preventing delays and mistakes.",
  },
  {
    number: "02",
    title: "Real-Time Quality Checks",
    description:
      "Quality inspections are conducted at every stage of the project to maintain consistent construction standards.",
  },
  {
    number: "03",
    title: "Accurate Quantity Analysis",
    description:
      "Precise material estimation helps prevent excess purchasing and significantly reduces material waste.",
  },
  {
    number: "04",
    title: "Technology Driven Approach",
    description:
      "Modern tools and technologies are used wherever necessary to improve efficiency, transparency, and accuracy.",
  },
];

export default function NMBuildTechLanding() {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.05,
      smoothWheel: true,
      touchMultiplier: 1.1,
    });

    let animationFrame = 0;
    const raf = (time) => {
      lenis.raf(time);
      animationFrame = requestAnimationFrame(raf);
    };

    animationFrame = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(animationFrame);
      lenis.destroy();
    };
  }, []);

  const scrollToQuote = () => {
    document
      .getElementById("request-quote")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="paper-shell text-[#121212]" data-testid="nm-buildtech-landing-page">
      <section
        className="hero-blueprint relative min-h-screen overflow-hidden border-b border-[color:var(--border-architect)]"
        data-testid="hero-section"
        id="hero"
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.07] grayscale"
          style={{
            backgroundImage: `url(${HERO_BLUEPRINT})`,
            backgroundPosition: "center",
            backgroundSize: "cover",
          }}
        />
        <div className="absolute inset-0 bg-[#f5f5f0]/95" />

        <div className="relative mx-auto flex min-h-screen max-w-screen-2xl flex-col px-6 pb-14 pt-6 md:px-12 lg:px-20">
          <motion.header
            className="flex flex-col gap-5 border-b border-[color:var(--border-architect)] pb-5 md:flex-row md:items-center md:justify-between"
            data-testid="site-header"
            initial={{ opacity: 0, y: -18 }}
            transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
            animate={{ opacity: 1, y: 0 }}
          >
            <a href="#hero" data-testid="header-brand-link">
              <BrandLogo className="header-brand" iconClassName="h-16 w-[122px] md:h-20 md:w-[154px]" />
            </a>

            <nav
              className="flex flex-wrap items-center gap-3 md:justify-end"
              data-testid="header-navigation"
            >
              <a
                className="header-nav-link"
                data-testid="header-about-link"
                href="#about-us"
              >
                About Us
              </a>
              <a
                className="header-nav-link"
                data-testid="header-why-us-link"
                href="#why-us"
              >
                Why Us
              </a>
              <a
                className="header-nav-cta"
                data-testid="header-get-quote-link"
                href="#request-quote"
              >
                Get Quote
              </a>
            </nav>
          </motion.header>

          <div className="grid flex-1 items-center gap-14 py-16 lg:grid-cols-[1.15fr_0.85fr] lg:py-20">
            <SectionReveal className="max-w-4xl">
              <p className="eyebrow-label mb-6" data-testid="hero-eyebrow">
                Construction consulting and contracting
              </p>
              <h1
                className="font-heading text-5xl font-extrabold tracking-[-0.06em] text-[#121212] sm:text-6xl md:text-7xl lg:text-[7rem] lg:leading-[0.9]"
                data-testid="hero-headline"
              >
                Build Once
                <br />
                Build Right
              </h1>
              <p
                className="mt-8 max-w-2xl text-base leading-8 text-[#4d4d4d] sm:text-lg"
                data-testid="hero-subheadline"
              >
                NM BuildTech provides micro-level project coordination and
                real-time quality audits to eliminate rework, ensuring your
                vision is delivered with absolute cost efficiency.
              </p>

              <div className="mt-10 flex flex-col gap-5 sm:flex-row sm:items-center">
                <Button
                  className="h-auto rounded-none border border-[#121212] bg-[#121212] px-8 py-4 font-mono text-xs uppercase tracking-[0.28em] text-white transition-transform duration-300 hover:-translate-y-1 hover:bg-[#c0a062] hover:text-[#121212]"
                  data-testid="hero-request-quote-button"
                  onClick={scrollToQuote}
                  type="button"
                >
                  Request a Quote
                </Button>
                <p
                  className="font-mono text-xs uppercase tracking-[0.24em] text-[#6a6a6a]"
                  data-testid="hero-supporting-note"
                >
                  Precision-led oversight. Reduced waste. Fewer corrections.
                </p>
              </div>
            </SectionReveal>

            <SectionReveal className="justify-self-stretch lg:justify-self-end" delay={0.12}>
              <div
                className="section-frame relative overflow-hidden border border-[color:var(--border-architect)] bg-white p-6 md:p-8"
                data-testid="hero-visual-panel"
              >
                <div className="flex items-start justify-between gap-6 border-b border-[color:var(--border-architect)] pb-6">
                  <div>
                    <p className="eyebrow-label" data-testid="hero-panel-label">
                      Oversight philosophy
                    </p>
                    <p
                      className="mt-3 max-w-xs text-sm leading-7 text-[#555555]"
                      data-testid="hero-panel-description"
                    >
                      Detailed coordination and quality verification engineered
                      to protect your timeline, budget, and execution quality.
                    </p>
                  </div>
                  <span
                    className="font-mono text-xs uppercase tracking-[0.24em] text-[#c0a062]"
                    data-testid="hero-panel-code"
                  >
                    NM / 01
                  </span>
                </div>

                <div
                  className="my-8 flex items-center justify-center border border-[color:var(--border-architect)] bg-[#f7f7f2] py-10"
                  data-testid="hero-panel-logo"
                >
                  <BrandLogo iconClassName="h-[180px] w-[180px] max-w-full md:h-[220px] md:w-[220px]" layout="mark" />
                </div>

                <div className="grid gap-4 border-t border-[color:var(--border-architect)] pt-6">
                  {[
                    "Micro-level project coordination",
                    "Real-time quality audits",
                    "Accurate quantity analysis",
                  ].map((item, index) => (
                    <div
                      className="flex items-center justify-between gap-4 border-b border-dashed border-[color:var(--border-architect)] pb-4 last:border-b-0 last:pb-0"
                      data-testid={`hero-panel-capability-${index + 1}`}
                      key={item}
                    >
                      <p className="text-sm text-[#3f3f3f]">{item}</p>
                      <span className="font-mono text-xs text-[#7a7a7a]">0{index + 1}</span>
                    </div>
                  ))}
                </div>
              </div>
            </SectionReveal>
          </div>
        </div>
      </section>

      <section
        className="relative border-b border-[color:var(--border-architect)] px-6 py-24 md:px-12 md:py-32 lg:px-20"
        data-testid="about-section"
        id="about-us"
      >
        <div className="mx-auto grid max-w-screen-2xl gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20">
          <SectionReveal className="relative">
            <div
              className="mb-8"
              data-testid="about-logo"
            >
              <BrandLogo iconClassName="h-24 w-[170px] md:h-28 md:w-[200px]" layout="full" />
            </div>
            <p className="eyebrow-label mb-5" data-testid="about-eyebrow">
              About NM BuildTech
            </p>
            <h2
              className="font-heading text-4xl font-semibold tracking-[-0.05em] text-[#121212] md:text-5xl lg:text-6xl"
              data-testid="about-heading"
            >
              Precision-first construction oversight for projects that need to
              be right the first time.
            </h2>
          </SectionReveal>

          <SectionReveal className="space-y-8" delay={0.1}>
            <p className="text-base leading-8 text-[#4b4b4b] md:text-lg" data-testid="about-paragraph-1">
              NM BuildTech is a construction consulting and contracting firm
              focused on delivering projects with precision, efficiency, and
              uncompromising quality.
            </p>
            <p className="text-base leading-8 text-[#4b4b4b] md:text-lg" data-testid="about-paragraph-2">
              We believe construction should be done right the first time.
              Through micro-level coordination, modern construction
              technologies, and systematic quality audits, we ensure every
              stage of a project meets the highest standards.
            </p>

            <div className="grid gap-4 sm:grid-cols-2" data-testid="about-philosophy-grid">
              {philosophyPoints.map((point, index) => (
                <div
                  className="border border-[color:var(--border-architect)] bg-white p-5"
                  data-testid={`about-philosophy-point-${index + 1}`}
                  key={point}
                >
                  <p className="font-mono text-xs uppercase tracking-[0.24em] text-[#c0a062]">
                    0{index + 1}
                  </p>
                  <p className="mt-4 text-sm leading-7 text-[#3f3f3f] md:text-base">
                    {point}
                  </p>
                </div>
              ))}
            </div>
          </SectionReveal>
        </div>
      </section>

      <section
        className="relative border-b border-[color:var(--border-architect)] px-6 py-24 md:px-12 md:py-32 lg:px-20"
        data-testid="why-us-section"
        id="why-us"
      >
        <div className="mx-auto max-w-screen-2xl">
          <SectionReveal className="mb-14 max-w-3xl">
            <div
              className="mb-8"
              data-testid="why-us-logo"
            >
              <BrandLogo iconClassName="h-20 w-[145px] md:h-24 md:w-[175px]" layout="full" />
            </div>
            <p className="eyebrow-label mb-5" data-testid="why-us-eyebrow">
              Why Us
            </p>
            <h2
              className="font-heading text-4xl font-semibold tracking-[-0.05em] text-[#121212] md:text-5xl lg:text-6xl"
              data-testid="why-us-heading"
            >
              Structured site intelligence that reduces waste, rework, and
              avoidable execution risk.
            </h2>
          </SectionReveal>

          <motion.div
            className="grid gap-5 md:grid-cols-2 xl:grid-cols-4"
            data-testid="why-us-card-grid"
            initial="hidden"
            variants={staggerContainer}
            whileInView="visible"
            viewport={{ once: true, amount: 0.18 }}
          >
            {differentiators.map((item, index) => (
              <motion.article
                className="lift-card border border-[color:var(--border-architect)] bg-white p-7 md:p-8"
                custom={index * 0.06}
                data-testid={`why-us-card-${index + 1}`}
                key={item.title}
                variants={fadeUp}
              >
                <div className="mb-8 flex items-center justify-between border-b border-[color:var(--border-architect)] pb-4">
                  <p
                    className="font-mono text-xs uppercase tracking-[0.26em] text-[#c0a062]"
                    data-testid={`why-us-card-number-${index + 1}`}
                  >
                    {item.number}
                  </p>
                  <div className="h-px w-16 bg-[#121212]" />
                </div>
                <h3
                  className="font-heading text-2xl font-semibold tracking-[-0.04em] text-[#121212]"
                  data-testid={`why-us-card-title-${index + 1}`}
                >
                  {item.title}
                </h3>
                <p
                  className="mt-4 text-sm leading-7 text-[#4f4f4f] md:text-base"
                  data-testid={`why-us-card-description-${index + 1}`}
                >
                  {item.description}
                </p>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </section>

      <section
        className="relative px-6 py-24 md:px-12 md:py-32 lg:px-20"
        data-testid="request-quote-section"
        id="request-quote"
      >
        <div className="mx-auto grid max-w-screen-2xl gap-12 lg:grid-cols-[0.7fr_1fr] lg:gap-20">
          <SectionReveal className="max-w-xl">
            <div
              className="mb-8"
              data-testid="request-quote-logo"
            >
              <BrandLogo iconClassName="h-24 w-[170px] md:h-28 md:w-[200px]" layout="full" />
            </div>
            <p className="eyebrow-label mb-5" data-testid="request-quote-eyebrow">
              Request a Quote
            </p>
            <h2
              className="font-heading text-4xl font-semibold tracking-[-0.05em] text-[#121212] md:text-5xl lg:text-6xl"
              data-testid="request-quote-heading"
            >
              Tell us about your project and we will review the scope with a
              precision-first lens.
            </h2>
            <p
              className="mt-6 text-base leading-8 text-[#4d4d4d] md:text-lg"
              data-testid="request-quote-description"
            >
              Share your basic project details and NM BuildTech will respond
              with a tailored consultation or quote path aligned with your
              build requirements.
            </p>
          </SectionReveal>

          <SectionReveal className="lg:pt-8" delay={0.12}>
            <QuoteForm />
          </SectionReveal>
        </div>
      </section>
    </main>
  );
}